import React, { useContext, useState } from "react";
import { Box, Button, Divider, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { ThemeContext } from "../Misc/ThemeContext";
import { Brightness4Outlined, ArrowDropDownOutlined, Edit, Add } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { t } from "i18next";

const ThemeSelector: React.FC = () => {
	const { theme, setTheme, customThemes } = useContext(ThemeContext);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const navigate = useNavigate();

	const handleThemeChange = (newTheme) => {
		setTheme(newTheme);
		localStorage.setItem("theme", newTheme);
		setAnchorEl(null);
	};

	const handleRedirect = (e, url) => {
		e.stopPropagation();
		setAnchorEl(null);
		navigate(url);
	};

	const renderCustomThemes = () => {
		return customThemes?.map((themeData) => {
			const isLongName = themeData.name.length > 12;
			const displayName = isLongName ? `${themeData.name.slice(0, 12)}...` : themeData.name;

			return (
				<MenuItem
					key={themeData.name}
					selected={theme === themeData.name}
					onClick={() => handleThemeChange(themeData.name)}
					id={`snr-ai-ThemeSelector-${themeData.name.toLowerCase()}Theme`}
					sx={{ display: "flex", justifyContent: "space-between" }}
				>
					<Tooltip title={isLongName ? themeData.name : ""} arrow>
						<span>{displayName}</span>
					</Tooltip>

					<Button
						size="small"
						variant={theme === themeData.name ? "contained" : "outlined"}
						onClick={(e) =>
							handleRedirect(
								e,
								`/company-settings/settings/appearance?theme=${themeData?._id}`
							)
						}
						sx={{
							ml: 1.5,
							bgcolor: "transparent",
							minWidth: "auto",
							width: "28px",
							borderRadius: "50%",
						}}
					>
						<Edit
							sx={{
								fontSize: "16px",
								color: theme !== themeData.name && "gray",
							}}
						/>
					</Button>
				</MenuItem>
			);
		});
	};

	return (
		<>
			<Tooltip title="Select Theme">
				<Button
					startIcon={<Brightness4Outlined />}
					endIcon={<ArrowDropDownOutlined />}
					onClick={(e) => setAnchorEl(e.currentTarget)}
					sx={{ color: "white", bgcolor: "transparent" }}
					id="snr-ai-ThemeSelector-themeButton"
				>
					{theme}
				</Button>
			</Tooltip>
			<Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
				<Box maxHeight="260px" overflow="auto">
					<MenuItem
						selected={theme === "Purple"}
						onClick={() => handleThemeChange("Purple")}
						id="snr-ai-ThemeSelector-purpleTheme"
					>
						Purple
					</MenuItem>
					<MenuItem
						selected={theme === "dark"}
						onClick={() => handleThemeChange("dark")}
						id="snr-ai-ThemeSelector-darkTheme"
					>
						Dark
					</MenuItem>
					<MenuItem
						selected={theme === "Orange"}
						onClick={() => handleThemeChange("Orange")}
						id="snr-ai-ThemeSelector-orangeTheme"
					>
						Orange
					</MenuItem>
					<MenuItem
						selected={theme === "Brown"}
						onClick={() => handleThemeChange("Brown")}
						id="snr-ai-ThemeSelector-brownTheme"
					>
						Brown
					</MenuItem>

					{renderCustomThemes()}
				</Box>

				<Divider />

				<MenuItem
					onClick={() => navigate("/company-settings/settings/appearance")}
					id="snr-ai-ThemeSelector-addNewTheme"
					sx={{ marginTop: "6px" }}
				>
					<IconButton
						aria-label="edit"
						onClick={(e) => handleRedirect(e, `/company-settings/settings/appearance`)}
						sx={{ padding: 0 }}
					>
						<Add />
					</IconButton>
					{t("Add New")}
				</MenuItem>
			</Menu>
		</>
	);
};

export default ThemeSelector;
